// User types
export interface User {
  _id: string;
  email: string;
  user_type: 'internal' | 'external';
  status: 'active' | 'inactive' | 'pending' | 'suspended';
  profile: {
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  };
  fullName: string;
  last_login?: string;
  created_at: string;
  updated_at: string;
}

// Organization types
export interface OrgPublicInfo {
  description?: string;
  email?: string;
  phone?: string;
  address?: { street?: string; city?: string; state?: string; zip_code?: string; country?: string };
  social_media?: { instagram?: string; facebook?: string; twitter?: string; linkedin?: string; tiktok?: string };
}

export interface Organization {
  _id: string;
  name: string;
  slug: string;
  database_name: string;
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  // Tipo de organización (Tejedores, VIDA-296). 'weaver' = colaborador ligero,
  // con panel reducido. Ausente en docs antiguos = 'organization'.
  org_type?: 'organization' | 'weaver';
  config: {
    domain?: string;
    // URL pública de la web PET (autodetectada por el backend). La usa la vista
    // previa en vivo del Page Builder.
    public_site_url?: string;
    // Orígenes desde los que los frontends de esta org pueden llamar a la API
    // desde el navegador (CORS). Esquema + host + puerto, sin path ni barra final.
    allowed_origins?: string[];
    // Mensaje que la organización muestra a sus miembros en la home del panel.
    welcome_text?: string;
    locales: string[];
    default_locale: string;
    cloudinary_cloud?: string;
    storage?: {
      bucket_name: string;
      public_url: string;
    };
    features: {
      blog: boolean;
      ecommerce: boolean;
      catalog: boolean;
      multilingual: boolean;
      analytics: boolean;
      planner?: boolean;
      processes?: boolean;
    };
    // Datos de contacto públicos (VIDA-359): los leen las webs de catálogo y tienda.
    public_info?: OrgPublicInfo;
    theme?: {
      primary_color?: string;
      logo_url?: string;
    };
    // Autofichaje del trabajador desde el panel (VIDA-256). OPT-IN.
    presence?: {
      self_clock_enabled?: boolean;
      self_clock_geolocation?: boolean;
    };
    // Toggles del bloque Gestion (Asociados/Cuotas/Presupuesto). Si una
    // propiedad es undefined se trata como habilitado (legacy default).
    management?: {
      members?: { enabled?: boolean };
      fees?: { enabled?: boolean };
      budget?: {
        enabled?: boolean;
        transparency?: 'full' | 'aggregated_only';
        currency?: string;
      };
      // Reuniones IA (juntas con transcripcion + resumen). OPT-IN: off por
      // defecto; la org decide si permite su uso.
      meetings?: { enabled?: boolean };
      // CRM (contactos multi-fuente, empresas, propuestas, comunicaciones). OPT-IN.
      crm?: { enabled?: boolean };
    };
  };
  owner_id: string | User;
  created_at: string;
  updated_at: string;
}

// Role types
export interface Role {
  _id: string;
  name: string;
  description?: string;
  organization_id: string | null;
  permissions: string[];
  is_system: boolean;
  created_at: string;
}

// User-Organization-Role assignment
export interface UserOrganizationRole {
  _id: string;
  user_id: string | User;
  organization_id: string | Organization;
  role_id: string | Role;
  created_at: string;
}

// Database types
export interface DatabaseInfo {
  organization: {
    id: string;
    name: string;
    slug: string;
    status: string;
  };
  database_name: string;
  stats: {
    size_bytes: number;
    storage_size: number;
    collections: number;
    documents: number;
    indexes: number;
  } | null;
  error?: string;
}

export interface DatabaseStats {
  database: string;
  stats: {
    size_bytes: number;
    storage_size: number;
    collections: number;
    documents: number;
    indexes: number;
    avg_doc_size: number;
  };
  collections: CollectionStats[];
}

export interface CollectionStats {
  name: string;
  documents: number;
  size: number;
  avgDocSize: number;
  indexes: number;
}

export interface Backup {
  name: string;
  size_bytes: number;
  created_at: string;
}

// System backup types
export interface SystemBackup {
  filename: string;
  type: 'mongodb' | 'full';
  size_bytes: number;
  created_at: string;
}

export interface BackupStats {
  total_backups: number;
  total_size: number;
  retention_days: number;
  by_type: {
    mongodb: number;
    full: number;
  };
  last_backup?: string;
}

// Server metrics types
export interface ServerMetrics {
  cpu: {
    usage: number;
    cores: number;
    model: string;
  };
  memory: {
    total: number;
    used: number;
    free: number;
    usage: number;
  };
  disk: {
    total: number;
    used: number;
    free: number;
    usage: number;
  };
  uptime: number;
  hostname: string;
  platform: string;
  load: number[];
}

export interface ServiceHealth {
  name: string;
  status: 'healthy' | 'unhealthy' | 'degraded' | 'unknown';
  responseTime?: number;
  lastCheck: string;
  details?: Record<string, unknown>;
}

// Audit log types
export interface AuditLog {
  _id: string;
  action: string;
  actor_id: string | User;
  organization_id?: string | Organization;
  resource_type: string;
  resource_id: string;
  details: Record<string, unknown>;
  ip_address?: string;
  timestamp: string;
}

// Auth types
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface LoginResponse {
  message: string;
  user: User;
  tokens: AuthTokens;
}

// API response types
export interface ApiResponse<T> {
  success?: boolean;
  message?: string;
  error?: string;
  data?: T;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

// Permission constants
export const PERMISSIONS = {
  ALL: '*',
  ORG_READ: 'org:read',
  ORG_CREATE: 'org:create',
  ORG_UPDATE: 'org:update',
  ORG_DELETE: 'org:delete',
  USERS_READ: 'users:read',
  USERS_CREATE: 'users:create',
  USERS_UPDATE: 'users:update',
  USERS_DELETE: 'users:delete',
  // Gestionar los roles de SU organización (crear/editar/borrar) y asignarlos
  // a sus usuarios. Los roles globales siguen siendo de super_admin.
  ROLES_MANAGE: 'roles:manage',
  CONTENT_READ: 'content:read',
  CONTENT_CREATE: 'content:create',
  CONTENT_UPDATE: 'content:update',
  CONTENT_DELETE: 'content:delete',

  // CMS (Page Builder / editores de sitio). Namespace dedicado; content:* = alias.
  CMS_READ: 'cms:read',
  CMS_CREATE: 'cms:create',
  CMS_UPDATE: 'cms:update',
  CMS_DELETE: 'cms:delete',
  CMS_PUBLISH: 'cms:publish',
  MEDIA_READ: 'media:read',
  MEDIA_CREATE: 'media:create',
  MEDIA_DELETE: 'media:delete',
  SETTINGS_READ: 'settings:read',
  SETTINGS_UPDATE: 'settings:update',
  DB_READ: 'db:read',
  DB_BACKUP: 'db:backup',
  DB_RESTORE: 'db:restore',
  DB_CREATE: 'db:create',
  DB_DELETE: 'db:delete',
  AUDIT_READ: 'audit:read',
  SYSTEM_READ: 'system:read',
  ECOMMERCE_READ: 'ecommerce:read',
  ECOMMERCE_UPDATE: 'ecommerce:update',
  ORDERS_READ: 'orders:read',
  ORDERS_UPDATE: 'orders:update',

  // Catalog management
  CATALOG_READ: 'catalog:read',
  CATALOG_UPDATE: 'catalog:update',

  // Product management (VIDA products)
  PRODUCTS_READ: 'products:read',
  PRODUCTS_CREATE: 'products:create',
  PRODUCTS_UPDATE: 'products:update',
  PRODUCTS_DELETE: 'products:delete',
  PRODUCTS_PUBLISH: 'products:publish',
  PRODUCTS_TAG: 'products:tag',

  // Project management
  PROJECTS_READ: 'projects:read',
  PROJECTS_CREATE: 'projects:create',
  PROJECTS_UPDATE: 'projects:update',
  PROJECTS_DELETE: 'projects:delete',
  PROJECTS_ASSIGN: 'projects:assign',

  // Metadata management
  METADATA_READ: 'metadata:read',
  METADATA_CREATE: 'metadata:create',
  METADATA_UPDATE: 'metadata:update',
  METADATA_DELETE: 'metadata:delete',
  METADATA_ADMIN: 'metadata:admin',
  METADATA_DELEGATE: 'metadata:delegate',

  // Blog (editorial content per organization)
  BLOG_READ: 'blog:read',
  BLOG_CREATE: 'blog:create',
  BLOG_UPDATE: 'blog:update',
  BLOG_DELETE: 'blog:delete',
  BLOG_PUBLISH: 'blog:publish',

  // Invite member to an organization
  ORG_INVITE: 'org:invite',

  // Use the local AI assistant (SEO/SEM/Marketing)
  AI_USE: 'ai:use',

  // Marketing (tracking pixels, UTM builder, campaign report)
  MARKETING_READ: 'marketing:read',
  MARKETING_MANAGE: 'marketing:manage',

  // Planner / resource control module
  PLANNER_READ: 'planner:read',
  PLANNER_MANAGE: 'planner:manage',

  // Process definition module
  PROCESSES_READ: 'processes:read',
  PROCESSES_MANAGE: 'processes:manage',

  // Presence control (gk2control)
  PRESENCE_READ: 'presence:read',
  PRESENCE_MANAGE: 'presence:manage',

  // Management module — Asociados / Cuotas / Presupuesto.
  MEMBERS_READ: 'members:read',
  MEMBERS_MANAGE: 'members:manage',
  FEES_READ: 'fees:read',
  FEES_MANAGE: 'fees:manage',
  FEES_SEND: 'fees:send',
  BUDGET_READ: 'budget:read',
  BUDGET_MANAGE: 'budget:manage',
  BUDGET_APPROVE: 'budget:approve',

  // Reuniones IA — grabaciones transcritas/resumidas, privadas por usuario.
  MEETINGS_READ: 'meetings:read',
  MEETINGS_MANAGE: 'meetings:manage',
  MEETINGS_SHARE: 'meetings:share',

  // CRM — contactos multi-fuente, empresas, propuestas, comunicaciones.
  CRM_READ: 'crm:read',
  CRM_MANAGE: 'crm:manage',

  // Solicitudes de colaborador («tejedores») llegadas desde las tiendas.
  // Permiso propio y no atado a ser admin de la organizacion: quien atiende las
  // altas no tiene por que poder tocar la configuracion ni los usuarios.
  WEAVERS_READ: 'weavers:read',
  WEAVERS_MANAGE: 'weavers:manage',

  // Colaboradores (Tejedores) — la ficha de la RELACIÓN con cada tejedor
  // (VIDA-297). Distinto de weavers:* (que atiende las solicitudes de alta).
  COLLABORATORS_READ: 'collaborators:read',
  COLLABORATORS_MANAGE: 'collaborators:manage',

  // Plataforma — «Equipo técnico VIDA» (VIDA-347). Se consultan con
  // hasPlatformPermission(), no con hasPermission(): no dependen de la org.
  PLATFORM_ORGS: 'platform:orgs',
  PLATFORM_APIKEYS: 'platform:apikeys',
  PLATFORM_USERS: 'platform:users',
  PLATFORM_SYSTEM: 'platform:system',
  PLATFORM_BACKUPS: 'platform:backups',
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];
